/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'
import type { PropertyType } from '../../screens/settings/properties/constants'

const baseURL = '/api/metadatatemplates'
const baseURL_v1 = '/api/v1/metadatatemplates'
const baseURL_v2 = '/api/v2/metadatatemplates'

export interface MetadataDefinition {
  defaultValue?: any
  defaultValues?: any[]
  options?: string[]
}

export interface UserType {
  id: number
  name: string
}

export interface Metadata {
  id: number
  name: string
  description?: string
  type: PropertyType
  definition?: MetadataDefinition
  readOnly?: boolean
  createdAt?: string
  createdBy?: UserType
  updatedAt?: string
  updatedBy?: UserType
}

export interface MetadataTableResponse {
  elements: Metadata[]
  totalElements: number
  totalPages: number
  pageNumber: number
  pageSize: number
}

export interface MetadataQueryParams {
  id?: number[]
  name?: string
  pageStart?: number
  pageSize?: number
  sortBy?: string
  sortDesc?: boolean
  _noLoadingSpinner?: boolean
  [key: string]: string | number | boolean | number[] | undefined
}

export interface PropertyTypeOption {
  opt: string
  id: PropertyType
  value: string
}

export interface MetadataUsage {
  totalUsage: number
  usageDetails?: any[]
}

export interface PickerOption {
  value: number
  label: string
}

/**
 * Get paginated list of metadata for async picker
 */
export const getOptionsForAsyncPicker = async (
  search: string,
  pageStart: number
): Promise<{ options: PickerOption[]; hasMore: boolean }> => {
  const params = {
    name: search || undefined,
    pageStart,
    pageSize: 25
  }

  console.log('getOptionsForAsyncPicker called with:', { search, pageStart, params })

  const response = await baseApiService.fetch<MetadataTableResponse>(`${baseURL_v2}/picker`, {
    params
  })

  console.log('getOptionsForAsyncPicker response:', response)

  // Calculate if there are more pages - same logic as watermarks
  const hasMore = response.elements.length > 0 && (pageStart + 1) * 25 < response.totalElements

  console.log('hasMore calculation:', {
    elementsLength: response.elements.length,
    pageStart,
    totalElements: response.totalElements,
    hasMore
  })

  // Map and sort options by label (case-insensitive) - same as watermarks
  const options = response.elements
    .map((item: Metadata) => ({
      value: item.id,
      label: item.name,
      type: item.type
    }))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))

  return {
    options,
    hasMore
  }
}

/**
 * Get all metadata for table with pagination and filtering
 */
export const getAllForTable = async (params: MetadataQueryParams): Promise<MetadataTableResponse> => {
  return baseApiService.fetch<MetadataTableResponse>(baseURL_v2, { params })
}

/**
 * Get all metadata (legacy compatibility)
 * Returns paginated response with elements array
 */
export const getAll = async (params?: MetadataQueryParams): Promise<MetadataTableResponse> => {
  return baseApiService.fetch<MetadataTableResponse>(baseURL, { params })
}

/**
 * Get available metadata types with translations
 */
export const getAvailableTypes = (translateFn: (key: string) => string): PropertyTypeOption[] => {
  const types: PropertyTypeOption[] = [
    { opt: 'date', id: 'DATETIME', value: translateFn('METADATA_TYPE_DATETIME') },
    { opt: 'text', id: 'STRING', value: translateFn('METADATA_TYPE_STRING') },
    { opt: 'number', id: 'NUMBER', value: translateFn('METADATA_TYPE_NUMBER') },
    {
      opt: 'multi-pick-list',
      id: 'SELECT_MULTI',
      value: translateFn('METADATA_TYPE_SELECT_MULTI')
    },
    { opt: 'pick-list', id: 'SELECT_ONE', value: translateFn('METADATA_TYPE_SELECT_ONE') },
    {
      opt: 'pick-list-user',
      id: 'SELECT_ONE_USER',
      value: translateFn('METADATA_TYPE_SELECT_ONE_USER')
    },
    {
      opt: 'multi-pick-list-user',
      id: 'SELECT_MULTI_USER',
      value: translateFn('METADATA_TYPE_SELECT_MULTI_USER')
    },
    {
      opt: 'pick-list-group',
      id: 'SELECT_ONE_GROUP',
      value: translateFn('METADATA_TYPE_SELECT_ONE_GROUP')
    },
    {
      opt: 'multi-pick-list-group',
      id: 'SELECT_MULTI_GROUP',
      value: translateFn('METADATA_TYPE_SELECT_MULTI_GROUP')
    }
  ]

  // Note: ARTIFACT type would be added based on etmfFeature.show in the original
  // For now, we'll add it by default
  types.push({
    opt: 'pick-list-artifact',
    id: 'ARTIFACT',
    value: translateFn('METADATA_TYPE_ARTIFACT')
  })

  return types.sort((a, b) => a.value.localeCompare(b.value))
}

/**
 * Get display name for a property type
 */
export const getNameByType = (
  type: PropertyType,
  translateFn: (key: string) => string
): PropertyTypeOption | undefined => {
  return getAvailableTypes(translateFn).find((t) => t.id === type)
}

/**
 * Get property usage metadata
 */
export const getPropertyUsageMetadata = async (metadataId: number): Promise<MetadataUsage> => {
  return baseApiService.fetch<MetadataUsage>(`${baseURL}/${metadataId}/usage`)
}

/**
 * Get single metadata by ID
 */
export const getById = async (metadataId: number): Promise<Metadata> => {
  return baseApiService.fetch<Metadata>(`${baseURL_v2}/${metadataId}`)
}

/**
 * Create new metadata
 */
export const create = async (data: Partial<Metadata>): Promise<Metadata> => {
  return baseApiService.fetch<Metadata>(baseURL, {
    method: 'POST',
    body: data
  })
}

/**
 * Update existing metadata
 */
export const update = async (metadataId: number, data: Partial<Metadata>): Promise<Metadata> => {
  return baseApiService.fetch<Metadata>(`${baseURL}/${metadataId}`, {
    method: 'PUT',
    body: data
  })
}

/**
 * Delete metadata
 */
export const remove = async (metadataId: number): Promise<void> => {
  await baseApiService.fetch<void>(`${baseURL}/${metadataId}`, {
    method: 'DELETE'
  })
}

/**
 * Get all metadata for picker
 */
export const getAllForPicker = async (params?: MetadataQueryParams): Promise<Metadata[]> => {
  const response = await baseApiService.fetch<Metadata[]>(`${baseURL}/all`, { params })
  return response.sort((a: Metadata, b: Metadata) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
}

/**
 * Get artifact values for a specific metadata and FSI
 */
export const getArtifactValues = async (metadataId: number, fsiId: number): Promise<any> => {
  return baseApiService.fetch(`${baseURL_v1}/${metadataId}/fsi/${fsiId}`)
}

/**
 * Get one property
 */
export const getOneProperty = async (metadataId: number): Promise<Metadata> => {
  return baseApiService.fetch<Metadata>(`${baseURL}/${metadataId}`)
}

/**
 * Export properties usage report
 */
export const exportPropertiesReports = async (filter: any, meta: any): Promise<void> => {
  const requestObj = { filter, meta }
  return baseApiService.fetch('/api/reports/propertiesusageexport', {
    method: 'POST',
    body: requestObj
  })
}
